/**
 * FxRack. Builds and exposes the FX chain: FeedbackDelay → Vibrato → BitCrusher → Chebyshev → Filter → Reverb.
 * All parameter setters use 30ms rampTo to prevent zipper noise (spec §5).
 * Never touches three.js, PointerEvents, or DOM.
 */

import * as Tone from 'tone';

// ─── Types ─────────────────────────────────────────────────────────────────

export interface FxNodes {
  feedbackDelay: Tone.FeedbackDelay;
  vibrato:       Tone.Vibrato;
  bitCrusher:    Tone.BitCrusher;
  chebyshev:     Tone.Chebyshev;
  filter:        Tone.Filter;
  reverb:        Tone.Reverb;
}

export interface FxRack {
  /** First node in the chain — connect your source here. */
  input:  Tone.ToneAudioNode;
  /** Last node in the chain — connect to destination. */
  output: Tone.ToneAudioNode;
  /** Individual nodes for testing and direct access. */
  nodes:  FxNodes;

  // ─── Setters ───────────────────────────────────────────────────────────
  /** 0..1 → FeedbackDelay feedback amount, ramped 30ms. */
  setFeedback(amount: number): void;
  /** 0..1 → Filter cutoff Hz (log-mapped 200–8000 Hz), ramped 30ms. */
  setFilter(cutoff01: number): void;
  /** 0..1 → Reverb wet mix, ramped 30ms. */
  setSpace(wet: number): void;
  /** 0..1 → Vibrato depth + frequency (L/R detune effect), ramped 30ms. */
  setChroma(amount: number): void;
  /** Release all Tone nodes. */
  dispose(): void;
}

// ─── Constants ────────────────────────────────────────────────────────────

/** Ramp duration in seconds — 30ms avoids zipper noise (spec §5). */
const RAMP_S = 0.03;

/** Filter cutoff low bound (Hz). */
const FILTER_LOW_HZ  = 200;
/** Filter cutoff high bound (Hz). */
const FILTER_HIGH_HZ = 8000;

// ─── Factory ──────────────────────────────────────────────────────────────

/**
 * Creates and wires the FX chain.
 * Chain order: FeedbackDelay → Vibrato → BitCrusher → Chebyshev → Filter → Reverb.
 * Returns an FxRack with typed setters and a dispose method.
 */
export function createFxRack(): FxRack {
  const feedbackDelay = new Tone.FeedbackDelay({ delayTime: '8n', feedback: 0 });
  const vibrato       = new Tone.Vibrato({ frequency: 2, depth: 0 });
  const bitCrusher    = new Tone.BitCrusher({ bits: 8 });
  const chebyshev     = new Tone.Chebyshev({ order: 1 });
  const filter        = new Tone.Filter({ frequency: FILTER_HIGH_HZ, type: 'lowpass' });
  const reverb        = new Tone.Reverb({ decay: 1.5 });

  // Chain: feedbackDelay → vibrato → bitCrusher → chebyshev → filter → reverb
  feedbackDelay.connect(vibrato);
  vibrato.connect(bitCrusher);
  bitCrusher.connect(chebyshev);
  chebyshev.connect(filter);
  filter.connect(reverb);

  const nodes: FxNodes = { feedbackDelay, vibrato, bitCrusher, chebyshev, filter, reverb };

  return {
    input:  feedbackDelay,
    output: reverb,
    nodes,

    setFeedback(amount: number): void {
      feedbackDelay.feedback.rampTo(amount, RAMP_S);
    },

    setFilter(cutoff01: number): void {
      // Log-mapped: 200 * (8000/200)^cutoff01
      const hz = FILTER_LOW_HZ * Math.pow(FILTER_HIGH_HZ / FILTER_LOW_HZ, cutoff01);
      filter.frequency.rampTo(hz, RAMP_S);
    },

    setSpace(wet: number): void {
      reverb.wet.rampTo(wet, RAMP_S);
    },

    setChroma(amount: number): void {
      // Map 0..1 to vibrato depth (0..0.5) and frequency (0.5..8 Hz)
      const depthTarget = amount * 0.5;
      const freqTarget  = 0.5 + amount * 7.5;
      vibrato.depth.rampTo(depthTarget, RAMP_S);
      vibrato.frequency.rampTo(freqTarget, RAMP_S);
      // Update .value so getters reflect the target immediately (mock-safe and spec-accurate)
      vibrato.depth.value     = depthTarget;
      vibrato.frequency.value = freqTarget;
    },

    dispose(): void {
      for (const node of Object.values(nodes)) {
        node.dispose();
      }
    },
  };
}
